import { IconButton } from '@brightdrop/bd-ui';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { AlertRecord } from '~/api/generated-api';
import { useDismissAlertV20250909Mutation } from '~/api/generated-api';
import CloseIcon from '~/assets/icons/new/close.svg?react';
import { selectActingProfileOrganization } from '~/store/profile/profileSlice.selectors';

interface NotificationDismissButtonProps {
  alert: AlertRecord;
  onError?: (error: Error) => void;
  onSuccess?: (alert: AlertRecord) => void;
}

const NotificationDismissButton = ({
  alert,
  onError,
  onSuccess,
}: NotificationDismissButtonProps): JSX.Element => {
  const [dismissAlert, { isLoading: isDismissing }] =
    useDismissAlertV20250909Mutation();
  const orgId = useSelector(selectActingProfileOrganization)?.id;
  const handleDismiss = useCallback(async () => {
    if (alert.id && orgId) {
      try {
        await dismissAlert({
          id: alert.id,
          'cs-organization': orgId,
        }).unwrap();
        onSuccess?.(alert);
      } catch (error) {
        onError?.(error as Error);
      }
    }
  }, [dismissAlert, alert, onError, onSuccess, orgId]);

  return (
    <IconButton
      onClick={handleDismiss}
      size="medium"
      variant="outline"
      icon={<CloseIcon />}
      data-testid={`notification-dismiss-button-${alert.id}`}
      disabled={isDismissing}
    />
  );
};

export default NotificationDismissButton;
